import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  FileText,
  Printer,
  Plus,
  Trash2,
  Eye,
  Loader2,
  UserPlus,
  Package
} from 'lucide-react';

const InvoicesPage = () => {
  const { API_URL, getAuthHeader, user } = useAuth();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar' || language === 'ku';

  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [formData, setFormData] = useState({
    customer_id: '',
    start_date: format(new Date(), 'yyyy-MM-01'),
    end_date: format(new Date(), 'yyyy-MM-dd'),
    tax_rate: '0',
    notes: ''
  });
  const [newCustomer, setNewCustomer] = useState({ name: '', shop_name: '', phone: '', address: '' });
  const [newProduct, setNewProduct] = useState({ name: '', breads_per_box: 10, default_price: 0 });
  const [savingCustomer, setSavingCustomer] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fc = (n) => new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

  const handleInvoicePrint = (inv) => {
    const printHTML = `
      <!DOCTYPE html>
      <html dir="ltr">
      <head>
        <meta charset="UTF-8">
        <title>Rechnung ${inv.invoice_number}</title>
        <style>
          @page { size: A4; margin: 15mm 20mm; }
          body { font-family: Arial, sans-serif; font-size: 11px; line-height: 1.5; margin: 0; padding: 20px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { padding: 8px; text-align: left; }
        </style>
      </head>
      <body>
        <div style="margin-bottom: 20px;">
          <div style="font-size: 18px; font-weight: bold;">${inv.company_name || 'Bäckerei'}</div>
          <div style="font-size: 10px; color: #666;">
            ${inv.company_address ? inv.company_address + ' | ' : ''}
            ${inv.company_phone ? 'Tel: ' + inv.company_phone + ' | ' : ''}
            ${inv.company_tax_id ? 'Steuernr.: ' + inv.company_tax_id : ''}
          </div>
        </div>
        
        <div style="margin-bottom: 30px; padding: 15px; border: 1px solid #e0e0e0;">
          <div style="font-size: 10px; color: #888;">Empfänger / Rechnungsadresse</div>
          <div style="font-weight: bold; font-size: 13px;">${inv.customer_name}</div>
          ${inv.customer_address ? '<div>' + inv.customer_address + '</div>' : ''}
        </div>
        
        <div style="display: flex; justify-content: space-between; margin-bottom: 25px;">
          <div>
            <h1 style="font-size: 22px; margin: 0;">RECHNUNG</h1>
          </div>
          <div style="text-align: right;">
            <div><strong>Rechnungsnr.:</strong> ${inv.invoice_number}</div>
            <div><strong>Rechnungsdatum:</strong> ${inv.invoice_date}</div>
            <div><strong>Leistungszeitraum:</strong> ${inv.delivery_period}</div>
          </div>
        </div>
        
        <table style="margin-bottom: 20px; border-bottom: 2px solid #333;">
          <thead>
            <tr style="background: #f5f5f5;">
              <th>Datum</th>
              <th>Bezeichnung</th>
              <th style="text-align: center;">Menge</th>
              <th style="text-align: right;">Einzelpreis</th>
              <th style="text-align: right;">Gesamt</th>
            </tr>
          </thead>
          <tbody>
            ${(inv.line_items || []).map(item => `
              <tr style="border-bottom: 1px solid #e0e0e0;">
                <td>${item.date}</td>
                <td>${item.product_name}</td>
                <td style="text-align: center;">${item.quantity} ${item.unit}</td>
                <td style="text-align: right;">${fc(item.unit_price)} €</td>
                <td style="text-align: right;">${fc(item.subtotal)} €</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div style="text-align: right; margin-bottom: 20px;">
          <div>Nettobetrag: ${fc(inv.subtotal)} €</div>
          <div>MwSt. ${inv.tax_rate || 0}%: ${fc(inv.tax_amount)} €</div>
          <div style="font-size: 14px; font-weight: bold; margin-top: 5px; padding-top: 5px; border-top: 2px solid #333;">
            Gesamtbetrag: ${fc(inv.grand_total)} €
          </div>
        </div>
        
        ${inv.notes ? '<div style="margin-top: 20px; padding: 10px; background: #f5f5f5;"><strong>Hinweis:</strong> ' + inv.notes + '</div>' : ''}
        
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `;
    
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    if (printWindow) {
      printWindow.document.write(printHTML);
      printWindow.document.close();
    }
  };

  const fetchData = async () => {
    try {
      const [invRes, custRes, prodRes] = await Promise.all([
        axios.get(`${API_URL}/api/invoices`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/customers`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/products`, { headers: getAuthHeader() })
      ]);
      setInvoices(invRes.data);
      setCustomers(custRes.data);
      setProducts(prodRes.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.customer_id) {
      toast.error(t('selectCustomer') || 'اختر زبون');
      return;
    }
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/invoices`, {
        ...formData,
        tax_rate: parseFloat(formData.tax_rate)
      }, { headers: getAuthHeader() });
      setSelectedInvoice(res.data);
      setShowCreate(false);
      toast.success(t('invoiceCreated') || 'تم إنشاء الفاتورة');
      fetchData();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Error';
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  const handleAddCustomer = async () => {
    if (!newCustomer.name.trim()) {
      toast.error(t('enterCustomerName') || 'أدخل اسم الزبون');
      return;
    }
    setSavingCustomer(true);
    try {
      const res = await axios.post(`${API_URL}/api/customers`, newCustomer, { headers: getAuthHeader() });
      toast.success(t('customerAdded') || 'تم إضافة الزبون');
      setCustomers([...customers, res.data]);
      setFormData({ ...formData, customer_id: res.data.id });
      setShowAddCustomer(false);
      setNewCustomer({ name: '', shop_name: '', phone: '', address: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSavingCustomer(false);
    }
  };

  const handleAddProduct = async () => {
    if (!newProduct.name.trim()) {
      toast.error(t('enterProductName') || 'أدخل اسم المنتج');
      return;
    }
    setSavingProduct(true);
    try {
      const res = await axios.post(`${API_URL}/api/products`, newProduct, { headers: getAuthHeader() });
      toast.success(t('productAdded') || 'تم إضافة المنتج');
      setProducts([...products, res.data]);
      setShowAddProduct(false);
      setNewProduct({ name: '', breads_per_box: 10, default_price: 0 });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error');
    } finally {
      setSavingProduct(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('deleteInvoiceConfirm') || 'حذف الفاتورة؟')) return;
    try {
      await axios.delete(`${API_URL}/api/invoices/${id}`, { headers: getAuthHeader() });
      toast.success(t('deleted') || 'تم الحذف');
      if (selectedInvoice?.id === id) setSelectedInvoice(null);
      fetchData();
    } catch (error) {
      toast.error('Error');
    }
  };

  // Invoice View (German Rechnung)
  if (selectedInvoice) {
    const inv = selectedInvoice;
    return (
      <div data-testid="invoice-view" dir="ltr">
        <div className="no-print flex gap-2 mb-4 justify-end">
          <Button variant="outline" onClick={() => setSelectedInvoice(null)} className="gap-2">
            <FileText className="w-4 h-4" /> {t('backToInvoices') || 'رجوع للفواتير'}
          </Button>
          <Button onClick={() => handleInvoicePrint(inv)} className="gap-2 bg-blue-600 hover:bg-blue-700">
            <Printer className="w-4 h-4" /> Drucken
          </Button>
        </div>
        
        <div className="max-w-[210mm] mx-auto bg-white border border-stone-200 shadow-lg" style={{fontFamily: 'Arial, Helvetica, sans-serif', padding: '30mm 25mm', fontSize: '11px', lineHeight: '1.5'}}>
          {/* Company Header */}
          <div style={{marginBottom: '20px'}}>
            <div style={{fontSize: '18px', fontWeight: 'bold', color: '#1a1a1a'}}>{inv.company_name || 'Bäckerei'}</div>
            <div style={{fontSize: '10px', color: '#666'}}>
              {inv.company_address && <span>{inv.company_address} | </span>}
              {inv.company_phone && <span>Tel: {inv.company_phone} | </span>}
              {inv.company_tax_id && <span>Steuernr.: {inv.company_tax_id}</span>}
            </div>
          </div>

          {/* Recipient */}
          <div style={{marginBottom: '30px', padding: '15px', border: '1px solid #e0e0e0', borderRadius: '4px'}}>
            <div style={{fontSize: '10px', color: '#888', marginBottom: '4px'}}>Empfänger / Rechnungsadresse</div>
            <div style={{fontWeight: 'bold', fontSize: '13px'}}>{inv.customer_name}</div>
            {inv.customer_address && <div>{inv.customer_address}</div>}
            {inv.customer_tax_id && <div>USt-IdNr.: {inv.customer_tax_id}</div>}
          </div>

          {/* Invoice Title + Meta */}
          <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '25px', alignItems: 'flex-start'}}>
            <div>
              <h1 style={{fontSize: '22px', fontWeight: 'bold', margin: '0 0 5px 0', color: '#1a1a1a'}}>RECHNUNG</h1>
              <div style={{fontSize: '10px', color: '#666'}}>Invoice</div>
            </div>
            <div style={{textAlign: 'right', fontSize: '11px'}}>
              <div><strong>Rechnungsnr.:</strong> {inv.invoice_number}</div>
              <div><strong>Rechnungsdatum:</strong> {inv.invoice_date}</div>
              <div><strong>Leistungszeitraum:</strong> {inv.delivery_period}</div>
            </div>
          </div>

          {/* Line Items Table */}
          <table style={{width: '100%', borderCollapse: 'collapse', marginBottom: '20px', fontSize: '10px'}}>
            <thead>
              <tr style={{backgroundColor: '#f5f5f5', borderBottom: '2px solid #333'}}>
                <th style={{padding: '8px', textAlign: 'left', fontWeight: 'bold'}}>Datum</th>
                <th style={{padding: '8px', textAlign: 'left', fontWeight: 'bold'}}>Bezeichnung</th>
                <th style={{padding: '8px', textAlign: 'center', fontWeight: 'bold'}}>Menge</th>
                <th style={{padding: '8px', textAlign: 'right', fontWeight: 'bold'}}>Einzelpreis</th>
                <th style={{padding: '8px', textAlign: 'right', fontWeight: 'bold'}}>Gesamt</th>
              </tr>
            </thead>
            <tbody>
              {inv.line_items?.map((item, idx) => (
                <tr key={idx} style={{borderBottom: '1px solid #e0e0e0'}}>
                  <td style={{padding: '6px 8px'}}>{item.date}</td>
                  <td style={{padding: '6px 8px'}}>{item.product_name}</td>
                  <td style={{padding: '6px 8px', textAlign: 'center'}}>{item.quantity} {item.unit}</td>
                  <td style={{padding: '6px 8px', textAlign: 'right'}}>{fc(item.unit_price)} €</td>
                  <td style={{padding: '6px 8px', textAlign: 'right', fontWeight: 'bold'}}>{fc(item.total)} €</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Totals */}
          <div style={{display: 'flex', justifyContent: 'flex-end', marginBottom: '25px'}}>
            <table style={{width: '280px', fontSize: '11px'}}>
              <tbody>
                <tr>
                  <td style={{padding: '5px 0'}}>Nettobetrag</td>
                  <td style={{padding: '5px 0', textAlign: 'right'}}>{fc(inv.net_total)} €</td>
                </tr>
                <tr style={{borderBottom: '1px solid #ccc'}}>
                  <td style={{padding: '5px 0'}}>USt. {inv.tax_rate}%</td>
                  <td style={{padding: '5px 0', textAlign: 'right'}}>{fc(inv.tax_amount)} €</td>
                </tr>
                <tr style={{fontWeight: 'bold', fontSize: '13px'}}>
                  <td style={{padding: '8px 0'}}>Bruttobetrag</td>
                  <td style={{padding: '8px 0', textAlign: 'right'}}>{fc(inv.gross_total)} €</td>
                </tr>
                {inv.total_paid > 0 && (
                  <>
                    <tr style={{color: '#059669'}}>
                      <td style={{padding: '3px 0'}}>Bereits bezahlt</td>
                      <td style={{padding: '3px 0', textAlign: 'right'}}>-{fc(inv.total_paid)} €</td>
                    </tr>
                    <tr style={{fontWeight: 'bold', fontSize: '14px', color: inv.amount_due > 0 ? '#dc2626' : '#059669', borderTop: '2px solid #333'}}>
                      <td style={{padding: '8px 0'}}>Offener Betrag</td>
                      <td style={{padding: '8px 0', textAlign: 'right'}}>{fc(inv.amount_due)} €</td>
                    </tr>
                  </>
                )}
              </tbody>
            </table>
          </div>

          {/* Notes */}
          {inv.notes && (
            <div style={{padding: '10px', backgroundColor: '#fafafa', borderRadius: '4px', fontSize: '10px', marginBottom: '20px'}}>
              <strong>Bemerkung:</strong> {inv.notes}
            </div>
          )}

          {/* Footer */}
          <div style={{borderTop: '1px solid #ccc', paddingTop: '15px', fontSize: '9px', color: '#888', textAlign: 'center'}}>
            <p>{inv.company_name} {inv.company_address ? `| ${inv.company_address}` : ''} {inv.company_phone ? `| Tel: ${inv.company_phone}` : ''}</p>
            {inv.company_tax_id && <p>Steuernummer: {inv.company_tax_id}</p>}
            <p style={{marginTop: '5px'}}>Bitte überweisen Sie den Rechnungsbetrag innerhalb von 14 Tagen.</p>
          </div>
        </div>

        <style>{`
          @media print {
            .no-print { display: none !important; }
            @page { size: A4; margin: 0; }
            body { background: white; }
          }
        `}</style>
      </div>
    );
  }

  // Invoice List View
  return (
    <div data-testid="invoices-page" className="space-y-5" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="flex items-center justify-between">
        <h1 className="text-xl md:text-2xl font-bold text-stone-900 flex items-center gap-2">
          <FileText className="w-6 h-6 text-blue-600" />
          {t('invoices') || 'الفواتير'} (Rechnungen)
        </h1>
        <Button onClick={() => setShowCreate(true)} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="create-invoice-btn">
          <Plus className="w-4 h-4" /> {t('createInvoice') || 'إنشاء فاتورة'}
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full"></div>
        </div>
      ) : invoices.length === 0 ? (
        <Card className="border-stone-200">
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-stone-300 mb-3" />
            <p className="text-stone-500">{t('noInvoices') || 'لا توجد فواتير'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {invoices.map(inv => (
            <Card key={inv.id} className="border-stone-200 hover:border-blue-200 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <span className="font-mono font-bold text-blue-700">{inv.invoice_number}</span>
                      <span className="font-medium text-stone-900">{inv.customer_name}</span>
                    </div>
                    <div className="flex gap-3 text-xs text-stone-500 mt-1">
                      <span>{inv.invoice_date}</span>
                      <span>{inv.delivery_period}</span>
                      <span className="font-bold text-stone-700">{fc(inv.gross_total)} €</span>
                      {inv.amount_due > 0 && (
                        <span className="text-red-600 font-bold">{t('outstanding') || 'مستحق'}: {fc(inv.amount_due)} €</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setSelectedInvoice(inv)} data-testid={`view-invoice-${inv.id}`}>
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => handleDelete(inv.id)} data-testid={`delete-invoice-${inv.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Invoice Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{t('createInvoice') || 'إنشاء فاتورة'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t('customerName') || 'الزبون'}</Label>
                <Button 
                  type="button" 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setShowAddCustomer(true)}
                  className="gap-1 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 h-7 px-2"
                >
                  <UserPlus className="w-4 h-4" />
                  {t('addCustomer') || 'زبون جديد'}
                </Button>
              </div>
              <Select value={formData.customer_id} onValueChange={(v) => setFormData({...formData, customer_id: v})}>
                <SelectTrigger data-testid="invoice-customer-select">
                  <SelectValue placeholder={t('selectCustomer') || 'اختر زبون'} />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('startDate') || 'من'}</Label>
                <Input type="date" value={formData.start_date} onChange={(e) => setFormData({...formData, start_date: e.target.value})} data-testid="invoice-start-date" />
              </div>
              <div className="space-y-2">
                <Label>{t('endDate') || 'إلى'}</Label>
                <Input type="date" value={formData.end_date} onChange={(e) => setFormData({...formData, end_date: e.target.value})} data-testid="invoice-end-date" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t('taxRate') || 'نسبة الضريبة'} (USt.)</Label>
              <Select value={formData.tax_rate} onValueChange={(v) => setFormData({...formData, tax_rate: v})}>
                <SelectTrigger data-testid="invoice-tax-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">0% ({t('taxFree') || 'بدون ضريبة'})</SelectItem>
                  <SelectItem value="7">7% (ermäßigt)</SelectItem>
                  <SelectItem value="19">19% (regulär)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t('notes') || 'ملاحظات'} (Bemerkung)</Label>
              <Input value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} data-testid="invoice-notes" />
            </div>
            
            {/* Quick Add Product Button */}
            <div className="border-t pt-3">
              <Button 
                type="button" 
                variant="outline" 
                size="sm" 
                onClick={() => setShowAddProduct(true)}
                className="gap-2 w-full text-amber-600 border-amber-300 hover:bg-amber-50"
              >
                <Package className="w-4 h-4" />
                {t('addProduct') || 'إضافة منتج جديد'}
              </Button>
            </div>
            
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowCreate(false)}>{t('cancel') || 'إلغاء'}</Button>
              <Button onClick={handleCreate} disabled={creating} className="bg-blue-600 hover:bg-blue-700 gap-2" data-testid="submit-invoice-btn">
                {creating && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('createInvoice') || 'إنشاء'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog open={showAddCustomer} onOpenChange={setShowAddCustomer}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-emerald-600" />
              {t('addCustomer') || 'إضافة زبون جديد'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('customerName') || 'اسم الزبون'} *</Label>
              <Input 
                value={newCustomer.name} 
                onChange={(e) => setNewCustomer({...newCustomer, name: e.target.value})}
                placeholder={t('customerName') || 'اسم الزبون'}
                data-testid="new-customer-name"
              />
            </div>
            <div className="space-y-2">
              <Label>{t('shopName') || 'اسم المحل'}</Label>
              <Input 
                value={newCustomer.shop_name} 
                onChange={(e) => setNewCustomer({...newCustomer, shop_name: e.target.value})}
                placeholder={t('shopName') || 'اسم المحل'}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('phone') || 'الهاتف'}</Label>
                <Input 
                  value={newCustomer.phone} 
                  onChange={(e) => setNewCustomer({...newCustomer, phone: e.target.value})}
                  placeholder="0123456789"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('address') || 'العنوان'}</Label>
                <Input 
                  value={newCustomer.address} 
                  onChange={(e) => setNewCustomer({...newCustomer, address: e.target.value})}
                  placeholder={t('address') || 'العنوان'}
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowAddCustomer(false)}>{t('cancel') || 'إلغاء'}</Button>
              <Button onClick={handleAddCustomer} disabled={savingCustomer} className="bg-emerald-600 hover:bg-emerald-700 gap-2">
                {savingCustomer && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('add') || 'إضافة'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Product Dialog */}
      <Dialog open={showAddProduct} onOpenChange={setShowAddProduct}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-600" />
              {t('addProduct') || 'إضافة منتج جديد'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>{t('productName') || 'اسم المنتج'} *</Label>
              <Input 
                value={newProduct.name} 
                onChange={(e) => setNewProduct({...newProduct, name: e.target.value})}
                placeholder={t('productName') || 'اسم المنتج'}
                data-testid="new-product-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t('breadsPerBox') || 'عدد الربطات'}</Label>
                <Input 
                  type="number"
                  value={newProduct.breads_per_box} 
                  onChange={(e) => setNewProduct({...newProduct, breads_per_box: parseInt(e.target.value) || 0})}
                  min="1"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('defaultPrice') || 'سعر البيع'} (€)</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={newProduct.default_price} 
                  onChange={(e) => setNewProduct({...newProduct, default_price: parseFloat(e.target.value) || 0})}
                  min="0"
                />
              </div>
            </div>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setShowAddProduct(false)}>{t('cancel') || 'إلغاء'}</Button>
              <Button onClick={handleAddProduct} disabled={savingProduct} className="bg-amber-600 hover:bg-amber-700 gap-2">
                {savingProduct && <Loader2 className="w-4 h-4 animate-spin" />}
                {t('add') || 'إضافة'}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoicesPage;
